import React, { memo, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import { useTranslation } from "react-i18next";
import colours from "../../theme/colours";
import { getPrimaryColour } from "../../utils/getPrimaryColour";

ChartJS.register(...registerables);

const PostExertionRespiratorySignalChart = memo(
  function PostExertionRespiratorySignalChart({
    detrendedPoints = [],
    smoothedPoints = [],
    height = 220,
    maxSecs = 60,
  }) {
    const { t } = useTranslation();

    const data = useMemo(
      () => ({
        datasets: [
          {
            label: t("result.resp.detrendedSignalLabel", "Detrended"),
            data: detrendedPoints.map((point) => ({
              x: point.t,
              y: point.value,
            })),
            parsing: false,
            borderColor: getPrimaryColour(),
            backgroundColor: "transparent",
            pointRadius: 0,
            pointHoverRadius: 0,
            borderWidth: 2,
            tension: 0.08,
          },
          {
            label: t("result.resp.smoothedSignalLabel", "Smoothed"),
            data: smoothedPoints.map((point) => ({
              x: point.t,
              y: point.value,
            })),
            parsing: false,
            borderColor: colours.secondary,
            backgroundColor: "transparent",
            pointRadius: 0,
            pointHoverRadius: 0,
            borderWidth: 2,
            tension: 0.2,
          },
        ],
      }),
      [detrendedPoints, smoothedPoints, t],
    );

    const options = useMemo(
      () => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "nearest",
          intersect: false,
        },
        plugins: {
          legend: {
            display: true,
            labels: {
              color: colours.typography,
              usePointStyle: true,
              boxWidth: 10,
            },
          },
          tooltip: {
            callbacks: {
              title(items) {
                const x = items?.[0]?.parsed?.x;
                return typeof x === "number" && Number.isFinite(x)
                  ? t("result.postExertion.decayTooltipTitle", {
                      defaultValue: "{{seconds}} s",
                      seconds: x.toFixed(1),
                    })
                  : "";
              },
            },
          },
        },
        scales: {
          x: {
            type: "linear",
            min: 0,
            max: maxSecs,
            title: {
              display: true,
              text: t("charts.signal.timeSeconds", "Time (s)"),
              color: colours.typography,
            },
            grid: {
              color: "rgba(147,147,147,0.2)",
            },
            ticks: {
              color: colours.typography,
            },
          },
          y: {
            grid: {
              color: "rgba(147,147,147,0.15)",
            },
            ticks: {
              display: false,
            },
            border: {
              display: false,
            },
          },
        },
      }),
      [maxSecs, t],
    );

    return (
      <div style={{ height }}>
        <Line data={data} options={options} />
      </div>
    );
  },
);

export default PostExertionRespiratorySignalChart;
